"use client";

import { Fragment, useState, useEffect } from "react";
import { Dialog, Listbox, Transition } from "@headlessui/react";
import { Link, FileText, AlignLeft, X, ChevronDown, Check } from "lucide-react";
import type { ResourceFormData, ResourceType } from "@/lib/types/resources";
import { ALLOWED_MIME_TYPES, MAX_FILE_SIZE } from "@/lib/constants/resources";
import { dynamic } from "@/src/lib/next-compat";

const TiptapEditor = dynamic(() => import("./TiptapEditor"), { ssr: false });

type ScopeType = "course" | "module" | "lesson";

interface Module {
    id: number;
    title: string;
    levelName: string | null;
    lessons: { id: number; title: string }[];
}

interface ResourceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: ResourceFormData | FormData) => Promise<void>;
    courseId: number;
    modules: Module[];
    initialData?: Partial<ResourceFormData> & { id?: number };
}

const TYPE_OPTIONS: { value: ResourceType; label: string; icon: React.ReactNode; description: string }[] = [
    {
        value: "LINK",
        label: "Link",
        icon: <Link className="h-4 w-4" />,
        description: "URL to a webpage, article, or video",
    },
    {
        value: "FILE",
        label: "File",
        icon: <FileText className="h-4 w-4" />,
        description: "PDF, Word, PowerPoint, or image",
    },
    {
        value: "RICH_TEXT",
        label: "Rich Text",
        icon: <AlignLeft className="h-4 w-4" />,
        description: "Write formatted content directly",
    },
];

function getInitialScope(data?: Partial<ResourceFormData>): ScopeType {
    if (data?.lesson_id) return "lesson";
    if (data?.module_id) return "module";
    return "course";
}

export default function ResourceModal({
    isOpen,
    onClose,
    onSave,
    courseId,
    modules,
    initialData,
}: ResourceModalProps) {
    const isEditing = !!initialData?.id;

    const [title, setTitle] = useState(initialData?.title ?? "");
    const [type, setType] = useState<ResourceType>(initialData?.type ?? "LINK");
    const [url, setUrl] = useState(initialData?.type === "LINK" ? (initialData.content ?? "") : "");
    const [richText, setRichText] = useState(initialData?.type === "RICH_TEXT" ? (initialData.content ?? "") : "");
    const [file, setFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [scope, setScope] = useState<ScopeType>(getInitialScope(initialData));
    const [selectedModuleId, setSelectedModuleId] = useState<number | null>(initialData?.module_id ?? null);
    const [selectedLessonId, setSelectedLessonId] = useState<number | null>(initialData?.lesson_id ?? null);

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setTitle(initialData?.title ?? "");
            setType(initialData?.type ?? "LINK");
            setUrl(initialData?.type === "LINK" ? (initialData.content ?? "") : "");
            setRichText(initialData?.type === "RICH_TEXT" ? (initialData.content ?? "") : "");
            setFile(null);
            setFileError(null);
            setError(null);
            setScope(getInitialScope(initialData));
            setSelectedModuleId(initialData?.module_id ?? null);
            setSelectedLessonId(initialData?.lesson_id ?? null);
        }
    }, [isOpen]);

    // All lessons flattened across modules for the lesson dropdown
    const allLessons = modules.flatMap((m) => {
        const prefix = m.levelName ? `${m.levelName} › ${m.title}` : m.title;
        return m.lessons.map((l) => ({ id: l.id, title: `${prefix} — ${l.title}` }));
    });

    const validateAndSetFile = (selected: File) => {
        setFileError(null);
        if (!ALLOWED_MIME_TYPES.includes(selected.type)) {
            setFileError("File type not supported. Allowed: PDF, Word, PowerPoint, Excel, images.");
            setFile(null);
            return;
        }
        if (selected.size > MAX_FILE_SIZE) {
            setFileError("File exceeds 10MB limit.");
            setFile(null);
            return;
        }
        setFile(selected);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0] ?? null;
        setFileError(null);

        if (!selected) { setFile(null); return; }

        if (!ALLOWED_MIME_TYPES.includes(selected.type)) {
            setFileError("File type not supported. Allowed: PDF, Word, PowerPoint, Excel, images.");
            setFile(null);
            return;
        }

        if (selected.size > MAX_FILE_SIZE) {
            setFileError("File exceeds 10MB limit.");
            setFile(null);
            return;
        }

        setFile(selected);
    };

    const isScopeValid = (): boolean => {
        if (scope === "module" && !selectedModuleId) return false;
        if (scope === "lesson" && !selectedLessonId) return false;
        return true;
    };

    const isValid = (): boolean => {
        if (!title.trim()) return false;
        if (type === "LINK" && !url.trim()) return false;
        if (type === "RICH_TEXT" && !richText.trim()) return false;
        if (type === "FILE" && !file && !isEditing) return false;
        if (!isScopeValid()) return false;
        return true;
    };

    const handleSave = async () => {
        if (!isValid()) return;
        setError(null);
        setIsSaving(true);

        try {
            if (type === "FILE" && file) {
                // For FILE with new file, send as FormData
                const formData = new FormData();
                formData.append("title", title.trim());
                formData.append("type", type);
                formData.append("resource_type", type);
                formData.append("file", file);
                formData.append("course_id", String(courseId));
                if (scope === "module" && selectedModuleId) formData.append("module_id", String(selectedModuleId));
                if (scope === "lesson" && selectedLessonId) formData.append("lesson_id", String(selectedLessonId));
                await onSave(formData);
            } else {
                // For LINK, RICH_TEXT, or FILE without new file
                const content = type === "LINK" ? url : type === "RICH_TEXT" ? richText : (initialData?.content ?? "");
                const data: ResourceFormData = {
                    title: title.trim(),
                    type,
                    content,
                    course_id: courseId,
                    module_id: scope === "module" ? selectedModuleId : null,
                    lesson_id: scope === "lesson" ? selectedLessonId : null,
                    file_name: type === "FILE" ? (initialData?.file_name ?? null) : null,
                    file_size: type === "FILE" ? (initialData?.file_size ?? null) : null,
                    mime_type: type === "FILE" ? (initialData?.mime_type ?? null) : null,
                };
                await onSave(data);
            }
            onClose();
        } catch (err: any) {
            setError(err?.message ?? "Something went wrong. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-slate-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative w-full transform rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:max-w-xl">
                                {/* Header */}
                                <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
                                    <Dialog.Title className="text-base font-semibold text-slate-900">
                                        {isEditing ? "Edit resource" : "Add resource"}
                                    </Dialog.Title>
                                    <button type="button" onClick={onClose} className="rounded-md p-1 text-slate-400 hover:text-slate-600">
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>

                                {/* Body */}
                                <div className="space-y-5 px-6 py-5 overflow-y-auto max-h-[70vh]">
                                    {/* Type selector */}
                                    <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Type</label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {TYPE_OPTIONS.map((opt) => (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() => setType(opt.value)}
                                                    className={`flex flex-col items-center gap-1.5 rounded-lg border px-3 py-3 text-center transition-colors ${
                                                        type === opt.value
                                                            ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                                                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                                                    }`}
                                                >
                                                    {opt.icon}
                                                    <span className="text-xs font-semibold">{opt.label}</span>
                                                    <span className="text-[10px] text-slate-400 leading-tight">{opt.description}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Title */}
                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-slate-700">Title</label>
                                        <input
                                            type="text"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            placeholder="Enter a title for this resource"
                                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        />
                                    </div>

                                    {/* Content — varies by type */}
                                    {type === "LINK" && (
                                        <div>
                                            <label className="mb-1.5 block text-sm font-medium text-slate-700">URL</label>
                                            <input
                                                type="url"
                                                value={url}
                                                onChange={(e) => setUrl(e.target.value)}
                                                placeholder="https://"
                                                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                            />
                                        </div>
                                    )}

                                    {type === "FILE" && (
                                        <div>
                                            <label className="mb-1.5 block text-sm font-medium text-slate-700">File</label>
                                            <div
                                                className={`rounded-md border-2 border-dashed px-4 py-10 text-center transition-colors ${
                                                    isDragging
                                                        ? "border-indigo-400 bg-indigo-50"
                                                        : "border-slate-300 bg-white hover:border-slate-400"
                                                }`}
                                                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                                onDragLeave={() => setIsDragging(false)}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    setIsDragging(false);
                                                    const dropped = e.dataTransfer.files[0];
                                                    if (dropped) validateAndSetFile(dropped);
                                                }}
                                            >
                                                <input
                                                    type="file"
                                                    id="resource-file-input"
                                                    className="sr-only"
                                                    onChange={handleFileChange}
                                                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp"
                                                />
                                                {file || (isEditing && initialData?.file_name) ? (
                                                    <div className="flex flex-col items-center gap-1">
                                                        <p className="text-sm font-medium text-slate-700">
                                                            {file ? file.name : initialData?.file_name}
                                                        </p>
                                                        <label htmlFor="resource-file-input" className="cursor-pointer text-xs text-indigo-600 hover:text-indigo-500">
                                                            Change file
                                                        </label>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center gap-1">
                                                        <p className="text-sm text-slate-500">
                                                            Drag & drop a file here, or{" "}
                                                            <label htmlFor="resource-file-input" className="cursor-pointer text-indigo-600 hover:text-indigo-500">
                                                                browse
                                                            </label>
                                                        </p>
                                                        <p className="text-xs text-slate-400">PDF, Word, PowerPoint, Excel, images — max 10MB</p>
                                                    </div>
                                                )}
                                            </div>
                                            {fileError && <p className="mt-1.5 text-xs text-red-600">{fileError}</p>}
                                        </div>
                                    )}

                                    {type === "RICH_TEXT" && (
                                        <div>
                                            <label className="mb-1.5 block text-sm font-medium text-slate-700">Content</label>
                                            <TiptapEditor content={richText} onChange={setRichText} placeholder="Write your content here..." />
                                        </div>
                                    )}

                                    {/* Scope / Tag */}
                                    <div>
                                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            Scope
                                        </label>
                                        <div className="flex gap-2">
                                            {(["course", "module", "lesson"] as ScopeType[]).map((s) => (
                                                <button
                                                    key={s}
                                                    type="button"
                                                    onClick={() => setScope(s)}
                                                    className={`rounded-lg border px-3 py-1.5 text-sm font-semibold capitalize transition-colors ${
                                                        scope === s
                                                            ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                                                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                                                    }`}
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>

                                        {scope === "module" && (
                                            <div className="mt-3">
                                                <Listbox value={selectedModuleId} onChange={setSelectedModuleId}>
                                                    {({ open }) => (
                                                    <div className="relative">
                                                        <Listbox.Button className="relative w-full cursor-pointer rounded-md border border-slate-300 bg-white py-2 pl-3 pr-10 text-left text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                                                            <span className={selectedModuleId ? "text-slate-900" : "text-slate-400"}>
                                                                {selectedModuleId
                                                                    ? (() => {
                                                                        const m = modules.find(m => m.id === selectedModuleId);
                                                                        return m ? (m.levelName ? `${m.levelName} › ${m.title}` : m.title) : "Select a module";
                                                                    })()
                                                                    : "Select a module"}
                                                            </span>
                                                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                                                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                                                            </span>
                                                        </Listbox.Button>
                                                        <Transition
                                                            as={Fragment}
                                                            leave="transition ease-in duration-100"
                                                            leaveFrom="opacity-100"
                                                            leaveTo="opacity-0"
                                                        >
                                                            <Listbox.Options className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg focus:outline-none">
                                                                {modules.map((m) => (
                                                                    <Listbox.Option
                                                                        key={m.id}
                                                                        value={m.id}
                                                                        className={({ active }) =>
                                                                            `relative cursor-pointer select-none py-2 pl-9 pr-4 text-sm ${active ? "bg-indigo-50 text-indigo-700" : "text-slate-700"}`
                                                                        }
                                                                    >
                                                                        {({ selected }) => (
                                                                            <>
                                                                                {selected && (
                                                                                    <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-indigo-600">
                                                                                        <Check className="h-4 w-4" />
                                                                                    </span>
                                                                                )}
                                                                                <div>
                                                                                    {m.levelName && (
                                                                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{m.levelName} › </span>
                                                                                    )}
                                                                                    {m.title}
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                    </Listbox.Option>
                                                                ))}
                                                            </Listbox.Options>
                                                        </Transition>
                                                    </div>
                                                    )}
                                                </Listbox>
                                            </div>
                                        )}

                                        {scope === "lesson" && (
                                            <div className="mt-3">
                                                <Listbox value={selectedLessonId} onChange={setSelectedLessonId}>
                                                    {({ open }) => (
                                                    <div className="relative">
                                                        <Listbox.Button className="relative w-full cursor-pointer rounded-md border border-slate-300 bg-white py-2 pl-3 pr-10 text-left text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                                                            <span className={selectedLessonId ? "text-slate-900" : "text-slate-400"}>
                                                                {selectedLessonId
                                                                    ? (allLessons.find(l => l.id === selectedLessonId)?.title ?? "Select a lesson")
                                                                    : "Select a lesson"}
                                                            </span>
                                                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                                                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                                                            </span>
                                                        </Listbox.Button>
                                                        <Transition
                                                            as={Fragment}
                                                            leave="transition ease-in duration-100"
                                                            leaveFrom="opacity-100"
                                                            leaveTo="opacity-0"
                                                        >
                                                            <Listbox.Options className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg focus:outline-none">
                                                                {allLessons.map((l) => (
                                                                    <Listbox.Option
                                                                        key={l.id}
                                                                        value={l.id}
                                                                        className={({ active }) =>
                                                                            `relative cursor-pointer select-none py-2 pl-9 pr-4 text-sm ${active ? "bg-indigo-50 text-indigo-700" : "text-slate-700"}`
                                                                        }
                                                                    >
                                                                        {({ selected }) => (
                                                                            <>
                                                                                {selected && (
                                                                                    <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-indigo-600">
                                                                                        <Check className="h-4 w-4" />
                                                                                    </span>
                                                                                )}
                                                                                {l.title}
                                                                            </>
                                                                        )}
                                                                    </Listbox.Option>
                                                                ))}
                                                            </Listbox.Options>
                                                        </Transition>
                                                    </div>
                                                    )}
                                                </Listbox>
                                            </div>
                                        )}

                                        {scope === "course" && (
                                            <p className="mt-2 text-xs text-slate-400">This resource isn't tied to a specific module or lesson.</p>
                                        )}
                                    </div>

                                    {error && <p className="text-sm text-red-600">{error}</p>}
                                </div>

                                {/* Footer */}
                                <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={isSaving}
                                        className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSave}
                                        disabled={!isValid() || isSaving}
                                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSaving ? "Saving..." : isEditing ? "Save changes" : "Add resource"}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
